import type { ReportPayload, ReportSuccess, SendResult } from "./types";

const MAX_BODY = 8 * 1024 * 1024;

export const ERROR_MESSAGES: Record<number, string> = {
  400: "Le rapport n'a pas pu être envoyé. Réessayez ; si le problème persiste, contactez l'agence.",
  401: "Configuration du widget invalide — contactez l'agence.",
  403: "Ce domaine n'est pas autorisé pour ce projet — contactez l'agence.",
  429: "Trop de signalements récents, réessayez dans un moment.",
};

export const NETWORK_ERROR_MESSAGE = "Envoi impossible. Vérifiez la connexion et réessayez.";

function buildForm(payload: ReportPayload, screenshot: Blob | null): FormData {
  const form = new FormData();
  form.append("payload", JSON.stringify(payload));
  if (screenshot) form.append("screenshot", screenshot, "capture.jpg");
  return form;
}

async function post(endpoint: string, form: FormData): Promise<Response> {
  // Aucun en-tête custom : le multipart cross-origin part sans preflight.
  return fetch(endpoint, { method: "POST", body: form });
}

/**
 * Envoie le rapport au tracker. En cas de 413 (ou de corps estimé > 8 Mo),
 * retente automatiquement une fois sans la capture.
 */
export async function sendReport(
  endpoint: string,
  payload: ReportPayload,
  screenshot: Blob | null,
): Promise<SendResult> {
  const payloadSize = new Blob([JSON.stringify(payload)]).size;
  let effectiveScreenshot = screenshot;
  let screenshotDropped = false;
  if (effectiveScreenshot && payloadSize + effectiveScreenshot.size > MAX_BODY - 64 * 1024) {
    effectiveScreenshot = null;
    screenshotDropped = true;
  }

  let response: Response;
  try {
    response = await post(endpoint, buildForm(payload, effectiveScreenshot));
  } catch {
    return { ok: false, message: NETWORK_ERROR_MESSAGE, retryable: true };
  }

  if (response.status === 413 && effectiveScreenshot) {
    screenshotDropped = true;
    try {
      response = await post(endpoint, buildForm(payload, null));
    } catch {
      return { ok: false, message: NETWORK_ERROR_MESSAGE, retryable: true };
    }
  }

  if (response.status === 201) {
    try {
      const bug = (await response.json()) as ReportSuccess;
      return { ok: true, bug, screenshotDropped };
    } catch {
      return { ok: true, bug: { bugId: "", url: "" }, screenshotDropped };
    }
  }

  const message = ERROR_MESSAGES[response.status] ?? ERROR_MESSAGES[400]!;
  return { ok: false, message, retryable: response.status === 400 || response.status === 429 || response.status === 413 };
}
